import * as THREE from "three";

export interface InputState {
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
}

export interface InputControllerOptions {
  camera: THREE.PerspectiveCamera;
  canvas: HTMLCanvasElement;
  chatInput: HTMLInputElement;
  onMove: (state: InputState) => void;
  // Hz at which onMove fires. Sends are also gated by "did anything change".
  sendHz?: number;
  moveSpeed?: number;
  sprintSpeed?: number;
  mouseSensitivity?: number;
}

/**
 * Pointer-lock + WASD/Space/Shift first-person controller.
 * Disables movement & mouse-look when the chat input is focused.
 * Yaw is on camera.rotation.y, pitch on camera.rotation.x (clamped).
 * Movement is camera-relative on the XZ plane (Y is world up, no flying tilt).
 */
export class InputController {
  private readonly camera: THREE.PerspectiveCamera;
  private readonly canvas: HTMLCanvasElement;
  private readonly chatInput: HTMLInputElement;
  private readonly onMove: (s: InputState) => void;
  private readonly sendIntervalMs: number;
  private readonly moveSpeed: number;
  private readonly sprintSpeed: number;
  private readonly mouseSensitivity: number;

  private locked = false;
  private chatFocused = false;

  private yaw = 0;
  private pitch = 0;
  private readonly keys = new Set<string>();

  private lastSendAt = 0;
  private lastSentPos = new THREE.Vector3(NaN, NaN, NaN);
  private lastSentRot = new THREE.Euler(NaN, NaN, NaN);

  private readonly tmpForward = new THREE.Vector3();
  private readonly tmpRight = new THREE.Vector3();
  private readonly tmpMove = new THREE.Vector3();

  constructor(opts: InputControllerOptions) {
    this.camera = opts.camera;
    this.canvas = opts.canvas;
    this.chatInput = opts.chatInput;
    this.onMove = opts.onMove;
    this.sendIntervalMs = 1000 / (opts.sendHz ?? 30);
    this.moveSpeed = opts.moveSpeed ?? 5;
    this.sprintSpeed = opts.sprintSpeed ?? 9;
    this.mouseSensitivity = opts.mouseSensitivity ?? 0.0022;

    this.attach();
  }

  private attach(): void {
    this.canvas.addEventListener("click", this.handleCanvasClick);
    document.addEventListener("pointerlockchange", this.handlePointerLockChange);
    document.addEventListener("pointerlockerror", this.handlePointerLockError);
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);

    this.chatInput.addEventListener("focus", this.handleChatFocus);
    this.chatInput.addEventListener("blur", this.handleChatBlur);
  }

  detach(): void {
    this.canvas.removeEventListener("click", this.handleCanvasClick);
    document.removeEventListener("pointerlockchange", this.handlePointerLockChange);
    document.removeEventListener("pointerlockerror", this.handlePointerLockError);
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
    this.chatInput.removeEventListener("focus", this.handleChatFocus);
    this.chatInput.removeEventListener("blur", this.handleChatBlur);
  }

  /** Set starting position/rotation (e.g. when the server tells us our spawn). */
  setPose(position: { x: number; y: number; z: number }, rotation: { x: number; y: number; z: number }): void {
    this.camera.position.set(position.x, position.y, position.z);
    this.yaw = rotation.y;
    this.pitch = rotation.x;
    this.applyRotation();
  }

  /** Per-frame update (call from render loop). dt in seconds. */
  update(dt: number): void {
    if (!this.chatFocused) this.applyMovement(dt);
    this.maybeEmit();
  }

  private applyMovement(dt: number): void {
    const sprint = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
    const speed = (sprint ? this.sprintSpeed : this.moveSpeed) * dt;

    this.tmpMove.set(0, 0, 0);

    // Forward: camera's -Z projected onto XZ.
    this.camera.getWorldDirection(this.tmpForward);
    this.tmpForward.y = 0;
    if (this.tmpForward.lengthSq() > 0) this.tmpForward.normalize();

    this.tmpRight.crossVectors(this.tmpForward, WORLD_UP).normalize();

    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) this.tmpMove.add(this.tmpForward);
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) this.tmpMove.sub(this.tmpForward);
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) this.tmpMove.add(this.tmpRight);
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) this.tmpMove.sub(this.tmpRight);

    if (this.tmpMove.lengthSq() > 0) {
      this.tmpMove.normalize().multiplyScalar(speed);
      this.camera.position.add(this.tmpMove);
    }

    if (this.keys.has("Space")) this.camera.position.y += speed;
    if (this.keys.has("ControlLeft") || this.keys.has("ControlRight")) this.camera.position.y -= speed;
  }

  private maybeEmit(): void {
    const now = performance.now();
    if (now - this.lastSendAt < this.sendIntervalMs) return;

    const pos = this.camera.position;
    const moved =
      this.lastSentPos.distanceToSquared(pos) > 1e-6 ||
      Math.abs(this.lastSentRot.x - this.pitch) > 1e-4 ||
      Math.abs(this.lastSentRot.y - this.yaw) > 1e-4;

    if (!moved) return;

    this.lastSentPos.copy(pos);
    this.lastSentRot.set(this.pitch, this.yaw, 0);
    this.lastSendAt = now;

    this.onMove({
      position: { x: pos.x, y: pos.y, z: pos.z },
      rotation: { x: this.pitch, y: this.yaw, z: 0 },
    });
  }

  private applyRotation(): void {
    // Pitch on X, yaw on Y, no roll. YXZ order so yaw rotates the world
    // around the global up axis (no banking when looking around).
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.camera.rotation.z = 0;
  }

  private handleCanvasClick = (): void => {
    if (this.chatFocused) return;
    if (!this.locked) {
      this.canvas.requestPointerLock();
    }
  };

  private handlePointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
  };

  private handlePointerLockError = (): void => {
    console.warn("[input] pointer lock failed");
  };

  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.locked || this.chatFocused) return;
    this.yaw -= e.movementX * this.mouseSensitivity;
    this.pitch -= e.movementY * this.mouseSensitivity;
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch));
    this.applyRotation();
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    // Special: T while game has focus → focus chat. ESC while chat focused → blur.
    if (!this.chatFocused && e.code === "KeyT") {
      e.preventDefault();
      this.chatInput.focus();
      return;
    }
    if (this.chatFocused && e.code === "Escape") {
      e.preventDefault();
      this.chatInput.blur();
      return;
    }
    if (this.chatFocused) return;
    this.keys.add(e.code);
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private handleChatFocus = (): void => {
    this.chatFocused = true;
    this.keys.clear();
    if (this.locked && document.exitPointerLock) document.exitPointerLock();
  };

  private handleChatBlur = (): void => {
    this.chatFocused = false;
  };
}

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const PITCH_LIMIT = Math.PI / 2 - 0.01;
